"use client";

import { useMemo, useCallback, memo } from "react";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import {
  useAppSelector,
  selectLoginStatus,
  store,
  setIsCommonSsoModalVisible,
} from "@repo/web-store";
import { componentNameConstants, dlEventConstants, dlElementConstants } from "@repo/analytics/data-layer.constants";

interface IpoDescriptionProps {
  ipoTitle: string;
  ipoDescription?: string;
}

// Move regex outside to avoid recompilation
const SIGN_IN_REGEX = /sign in/i;

function IpoDescription({ ipoTitle, ipoDescription }: IpoDescriptionProps) {
  const loginStatus = useAppSelector(selectLoginStatus);
  
  // Simplify login check - remove unnecessary state
  const isLoggedIn = loginStatus === "success" || loginStatus === "perpetual";

  const handleSignInClick = useCallback(() => {
    const sectionTitle = ipoTitle?.trim();
    const ctaText = ipoDescription?.trim();

    const data = {
      component: componentNameConstants.SIGN_IN_WITH_DESCRIPTION,
      event: dlEventConstants.CLICK,
      event_type: dlEventConstants.LINK_CLICK,
      element: dlElementConstants.SIGN_IN_WITH_DESCRIPTION_LINK_CLICK,
      section_title: sectionTitle,
      cta_text: ctaText,
    };
    dataLayer("", data);

    store.dispatch(setIsCommonSsoModalVisible(true));
  }, [ipoTitle, ipoDescription]);

  // Memoize expensive text splitting operation
  const parts = useMemo(() => {
    if (!ipoDescription) return [];
    return ipoDescription.split(SIGN_IN_REGEX);
  }, [ipoDescription]);

  if (isLoggedIn) return null;

  return (
    <p className="sign-in-text text-[12px] md:leading-[20px] align-middle text-[#1A1A1A]">
      {parts.map((part, index) => (
        <span key={index}>
          {part}
          {index < parts.length - 1 && (
            <button
              tabIndex={0}
              className="sign-in-button--ipo-title text-orange-500 cursor-pointer"
              onClick={handleSignInClick}
            >
              Sign in
            </button>
          )}
        </span>
      ))}
    </p>
  );
}

export default memo(IpoDescription);
